/**
 * Strategy Lab Store
 * ==================
 * Zustand state for the isolated Strategy Lab subsystem. Modeled on
 * `titanStore.ts`: `create<State>((set, get) => ({...}))`, every call goes
 * through the centralized `strategyLabApi` client (no direct fetch), optimistic
 * updates where safe, and `ApiError.detail` surfaced into `*Error` fields.
 *
 * Scope: this store owns the list-level entities (watchlists / scanners /
 * strategies / deployments), the scan-test result, the kill switch, and the
 * per-deployment recommendation lists. Ephemeral per-deployment positions/trades
 * (polled for display) are fetched directly in `DeploymentCard` via
 * `strategyLabApi`, mirroring how pages like FleetDashboard read telemetry.
 */

import { create } from 'zustand';
import { strategyLabApi, ApiError } from '../lib/api';
import type {
  Watchlist,
  Scanner,
  Strategy,
  Deployment,
  Recommendation,
  KillSwitchStatus,
  ScanTestRequest,
  ScanTestResponse,
  CreateWatchlistRequest,
  UpdateWatchlistRequest,
  CreateScannerRequest,
  UpdateScannerRequest,
  CreateStrategyRequest,
  UpdateStrategyRequest,
  CreateDeploymentRequest,
  UpdateDeploymentRequest,
} from '../types/strategyLab';

function errMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.detail;
  if (error instanceof Error) return error.message;
  return fallback;
}

interface StrategyLabState {
  // Watchlists
  watchlists: Watchlist[];
  watchlistsLoading: boolean;
  watchlistsError: string | null;

  // Scanners
  scanners: Scanner[];
  scannersLoading: boolean;
  scannersError: string | null;

  // Scan test (dry-run preview)
  scanTestResult: ScanTestResponse | null;
  scanTestLoading: boolean;
  scanTestError: string | null;

  // Strategies
  strategies: Strategy[];
  strategiesLoading: boolean;
  strategiesError: string | null;

  // Deployments
  deployments: Deployment[];
  deploymentsLoading: boolean;
  deploymentsError: string | null;

  // Per-deployment recommendations
  recommendations: Record<string, Recommendation[]>;

  // Kill switch
  killSwitch: KillSwitchStatus | null;
  killSwitchLoading: boolean;
  killSwitchError: string | null;

  // Last mutation error (shared surface for create/update/delete failures)
  actionError: string | null;
  clearActionError: () => void;

  // ── Watchlist actions ──
  fetchWatchlists: () => Promise<void>;
  createWatchlist: (body: CreateWatchlistRequest) => Promise<Watchlist | null>;
  updateWatchlist: (id: string, body: UpdateWatchlistRequest) => Promise<Watchlist | null>;
  deleteWatchlist: (id: string) => Promise<boolean>;
  importWatchlist: (id: string, text: string) => Promise<boolean>;

  // ── Scanner actions ──
  fetchScanners: () => Promise<void>;
  createScanner: (body: CreateScannerRequest) => Promise<Scanner | null>;
  updateScanner: (id: string, body: UpdateScannerRequest) => Promise<Scanner | null>;
  deleteScanner: (id: string) => Promise<boolean>;
  testScan: (body: ScanTestRequest) => Promise<ScanTestResponse | null>;
  clearScanTest: () => void;

  // ── Strategy actions ──
  fetchStrategies: () => Promise<void>;
  createStrategy: (body: CreateStrategyRequest) => Promise<Strategy | null>;
  updateStrategy: (id: string, body: UpdateStrategyRequest) => Promise<Strategy | null>;
  deleteStrategy: (id: string) => Promise<boolean>;

  // ── Deployment actions ──
  fetchDeployments: () => Promise<void>;
  createDeployment: (body: CreateDeploymentRequest) => Promise<Deployment | null>;
  updateDeployment: (id: string, body: UpdateDeploymentRequest) => Promise<Deployment | null>;
  pauseDeployment: (id: string) => Promise<boolean>;
  resumeDeployment: (id: string) => Promise<boolean>;
  undeployDeployment: (id: string) => Promise<boolean>;

  // ── Recommendation actions ──
  fetchRecommendations: (deploymentId: string) => Promise<void>;
  acceptRecommendation: (deploymentId: string, recId: string) => Promise<boolean>;
  dismissRecommendation: (deploymentId: string, recId: string) => Promise<boolean>;

  // ── Kill switch actions ──
  fetchKillSwitch: () => Promise<void>;
  activateKillSwitch: (closePositions?: boolean) => Promise<boolean>;
  resetKillSwitch: () => Promise<boolean>;
}

export const useStrategyLabStore = create<StrategyLabState>((set, get) => ({
  watchlists: [],
  watchlistsLoading: false,
  watchlistsError: null,

  scanners: [],
  scannersLoading: false,
  scannersError: null,

  scanTestResult: null,
  scanTestLoading: false,
  scanTestError: null,

  strategies: [],
  strategiesLoading: false,
  strategiesError: null,

  deployments: [],
  deploymentsLoading: false,
  deploymentsError: null,

  recommendations: {},

  killSwitch: null,
  killSwitchLoading: false,
  killSwitchError: null,

  actionError: null,
  clearActionError: () => set({ actionError: null }),

  // ── Watchlists ──
  fetchWatchlists: async () => {
    set({ watchlistsLoading: true, watchlistsError: null });
    try {
      const data = await strategyLabApi.listWatchlists();
      set({ watchlists: data.watchlists || [], watchlistsLoading: false });
    } catch (error) {
      set({ watchlistsError: errMessage(error, 'Failed to load watchlists'), watchlistsLoading: false });
    }
  },

  createWatchlist: async (body) => {
    set({ actionError: null });
    try {
      const created = await strategyLabApi.createWatchlist(body);
      await get().fetchWatchlists();
      return created;
    } catch (error) {
      set({ actionError: errMessage(error, 'Failed to create watchlist') });
      return null;
    }
  },

  updateWatchlist: async (id, body) => {
    set({ actionError: null });
    try {
      const updated = await strategyLabApi.updateWatchlist(id, body);
      await get().fetchWatchlists();
      return updated;
    } catch (error) {
      set({ actionError: errMessage(error, 'Failed to update watchlist') });
      return null;
    }
  },

  deleteWatchlist: async (id) => {
    const previous = get().watchlists;
    set({ actionError: null, watchlists: previous.filter((w) => w.id !== id) });
    try {
      await strategyLabApi.deleteWatchlist(id);
      return true;
    } catch (error) {
      set({ watchlists: previous, actionError: errMessage(error, 'Failed to delete watchlist') });
      return false;
    }
  },

  importWatchlist: async (id, text) => {
    set({ actionError: null });
    try {
      await strategyLabApi.importWatchlist(id, text);
      await get().fetchWatchlists();
      return true;
    } catch (error) {
      set({ actionError: errMessage(error, 'Failed to import symbols') });
      return false;
    }
  },

  // ── Scanners ──
  fetchScanners: async () => {
    set({ scannersLoading: true, scannersError: null });
    try {
      const data = await strategyLabApi.listScanners();
      set({ scanners: data.scanners || [], scannersLoading: false });
    } catch (error) {
      set({ scannersError: errMessage(error, 'Failed to load scanners'), scannersLoading: false });
    }
  },

  createScanner: async (body) => {
    set({ actionError: null });
    try {
      const created = await strategyLabApi.createScanner(body);
      await get().fetchScanners();
      return created;
    } catch (error) {
      set({ actionError: errMessage(error, 'Failed to create scanner') });
      return null;
    }
  },

  updateScanner: async (id, body) => {
    set({ actionError: null });
    try {
      const updated = await strategyLabApi.updateScanner(id, body);
      await get().fetchScanners();
      return updated;
    } catch (error) {
      set({ actionError: errMessage(error, 'Failed to update scanner') });
      return null;
    }
  },

  deleteScanner: async (id) => {
    const previous = get().scanners;
    set({ actionError: null, scanners: previous.filter((s) => s.id !== id) });
    try {
      await strategyLabApi.deleteScanner(id);
      return true;
    } catch (error) {
      set({ scanners: previous, actionError: errMessage(error, 'Failed to delete scanner') });
      return false;
    }
  },

  testScan: async (body) => {
    set({ scanTestLoading: true, scanTestError: null });
    try {
      const result = await strategyLabApi.testScan(body);
      set({ scanTestResult: result, scanTestLoading: false });
      return result;
    } catch (error) {
      set({ scanTestError: errMessage(error, 'Scan test failed'), scanTestLoading: false });
      return null;
    }
  },

  clearScanTest: () => set({ scanTestResult: null, scanTestError: null }),

  // ── Strategies ──
  fetchStrategies: async () => {
    set({ strategiesLoading: true, strategiesError: null });
    try {
      const data = await strategyLabApi.listStrategies();
      set({ strategies: data.strategies || [], strategiesLoading: false });
    } catch (error) {
      set({ strategiesError: errMessage(error, 'Failed to load strategies'), strategiesLoading: false });
    }
  },

  createStrategy: async (body) => {
    set({ actionError: null });
    try {
      const created = await strategyLabApi.createStrategy(body);
      await get().fetchStrategies();
      return created;
    } catch (error) {
      set({ actionError: errMessage(error, 'Failed to create strategy') });
      return null;
    }
  },

  updateStrategy: async (id, body) => {
    set({ actionError: null });
    try {
      const updated = await strategyLabApi.updateStrategy(id, body);
      await get().fetchStrategies();
      return updated;
    } catch (error) {
      set({ actionError: errMessage(error, 'Failed to update strategy') });
      return null;
    }
  },

  deleteStrategy: async (id) => {
    const previous = get().strategies;
    set({ actionError: null, strategies: previous.filter((s) => s.id !== id) });
    try {
      await strategyLabApi.deleteStrategy(id);
      return true;
    } catch (error) {
      set({ strategies: previous, actionError: errMessage(error, 'Failed to delete strategy') });
      return false;
    }
  },

  // ── Deployments ──
  fetchDeployments: async () => {
    set({ deploymentsLoading: true, deploymentsError: null });
    try {
      const data = await strategyLabApi.listDeployments();
      set({ deployments: data.deployments || [], deploymentsLoading: false });
    } catch (error) {
      set({ deploymentsError: errMessage(error, 'Failed to load deployments'), deploymentsLoading: false });
    }
  },

  createDeployment: async (body) => {
    set({ actionError: null });
    try {
      const created = await strategyLabApi.createDeployment(body);
      await get().fetchDeployments();
      return created;
    } catch (error) {
      set({ actionError: errMessage(error, 'Failed to deploy strategy') });
      return null;
    }
  },

  updateDeployment: async (id, body) => {
    set({ actionError: null });
    try {
      const updated = await strategyLabApi.updateDeployment(id, body);
      await get().fetchDeployments();
      return updated;
    } catch (error) {
      set({ actionError: errMessage(error, 'Failed to update deployment') });
      return null;
    }
  },

  pauseDeployment: async (id) => {
    // Optimistic status flip; reconcile from server on refetch.
    const previous = get().deployments;
    set({
      actionError: null,
      deployments: previous.map((d) => (d.id === id ? { ...d, status: 'paused', paused: true } : d)),
    });
    try {
      await strategyLabApi.pauseDeployment(id);
      await get().fetchDeployments();
      return true;
    } catch (error) {
      set({ deployments: previous, actionError: errMessage(error, 'Failed to pause deployment') });
      return false;
    }
  },

  resumeDeployment: async (id) => {
    const previous = get().deployments;
    set({
      actionError: null,
      deployments: previous.map((d) => (d.id === id ? { ...d, status: 'active', paused: false } : d)),
    });
    try {
      await strategyLabApi.resumeDeployment(id);
      await get().fetchDeployments();
      return true;
    } catch (error) {
      set({ deployments: previous, actionError: errMessage(error, 'Failed to resume deployment') });
      return false;
    }
  },

  undeployDeployment: async (id) => {
    const previous = get().deployments;
    set({ actionError: null, deployments: previous.filter((d) => d.id !== id) });
    try {
      await strategyLabApi.undeployDeployment(id);
      await get().fetchDeployments();
      return true;
    } catch (error) {
      set({ deployments: previous, actionError: errMessage(error, 'Failed to undeploy') });
      return false;
    }
  },

  // ── Recommendations ──
  fetchRecommendations: async (deploymentId) => {
    try {
      const data = await strategyLabApi.listRecommendations(deploymentId);
      set((state) => ({
        recommendations: { ...state.recommendations, [deploymentId]: data.recommendations || [] },
      }));
    } catch (error) {
      // Advisory data — surface via actionError but don't wipe existing cards.
      set({ actionError: errMessage(error, 'Failed to load recommendations') });
    }
  },

  acceptRecommendation: async (deploymentId, recId) => {
    set({ actionError: null });
    try {
      await strategyLabApi.acceptRecommendation(deploymentId, recId);
      await Promise.all([get().fetchRecommendations(deploymentId), get().fetchDeployments()]);
      return true;
    } catch (error) {
      set({ actionError: errMessage(error, 'Failed to accept recommendation') });
      return false;
    }
  },

  dismissRecommendation: async (deploymentId, recId) => {
    set({ actionError: null });
    // Optimistic removal from the card list.
    const prev = get().recommendations[deploymentId] || [];
    set((state) => ({
      recommendations: {
        ...state.recommendations,
        [deploymentId]: prev.filter((r) => r.id !== recId),
      },
    }));
    try {
      await strategyLabApi.dismissRecommendation(deploymentId, recId);
      return true;
    } catch (error) {
      set((state) => ({
        recommendations: { ...state.recommendations, [deploymentId]: prev },
        actionError: errMessage(error, 'Failed to dismiss recommendation'),
      }));
      return false;
    }
  },

  // ── Kill switch ──
  fetchKillSwitch: async () => {
    set({ killSwitchLoading: true, killSwitchError: null });
    try {
      const data = await strategyLabApi.killSwitch();
      set({ killSwitch: data, killSwitchLoading: false });
    } catch (error) {
      set({ killSwitchError: errMessage(error, 'Failed to load kill switch'), killSwitchLoading: false });
    }
  },

  activateKillSwitch: async (closePositions = false) => {
    set({ actionError: null });
    try {
      const data = await strategyLabApi.activateKillSwitch(closePositions);
      set({ killSwitch: data });
      await get().fetchDeployments();
      return true;
    } catch (error) {
      set({ actionError: errMessage(error, 'Failed to trip kill switch') });
      return false;
    }
  },

  resetKillSwitch: async () => {
    set({ actionError: null });
    try {
      const data = await strategyLabApi.resetKillSwitch();
      set({ killSwitch: data });
      return true;
    } catch (error) {
      set({ actionError: errMessage(error, 'Failed to reset kill switch') });
      return false;
    }
  },
}));
